package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import com.swetdot.swetgram.domain.MessageAudience
import com.swetdot.swetgram.domain.PresenceAudience
import com.swetdot.swetgram.domain.PrivacySettings
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.profile_blocked_users_title
import swetgram.shared.generated.resources.profile_privacy_allow_profile_link
import swetgram.shared.generated.resources.profile_privacy_allow_profile_link_supporting
import swetgram.shared.generated.resources.profile_privacy_audience_everyone
import swetgram.shared.generated.resources.profile_privacy_audience_existing_chats
import swetgram.shared.generated.resources.profile_privacy_audience_nobody
import swetgram.shared.generated.resources.profile_privacy_audience_shared_groups
import swetgram.shared.generated.resources.profile_privacy_message_footnote
import swetgram.shared.generated.resources.profile_privacy_no_e2e
import swetgram.shared.generated.resources.profile_privacy_section_profile_link
import swetgram.shared.generated.resources.profile_privacy_section_safety
import swetgram.shared.generated.resources.profile_privacy_show_last_seen
import swetgram.shared.generated.resources.profile_privacy_show_date_of_birth
import swetgram.shared.generated.resources.profile_privacy_show_location
import swetgram.shared.generated.resources.profile_privacy_show_online
import swetgram.shared.generated.resources.profile_privacy_title
import swetgram.shared.generated.resources.profile_privacy_unavailable_body
import swetgram.shared.generated.resources.profile_privacy_unavailable_title
import swetgram.shared.generated.resources.profile_privacy_who_can_message
import swetgram.shared.generated.resources.profile_try_again

/**
 * Privacy -- SPS §16.1.
 *
 * Four controls and a way into the blocked list, which is exactly what the spec lists. The labels
 * are adjusted as §16.1 permits ("Exact labels can be adjusted during UI design"): the spec writes
 * the middle option as "Contacts or Chats", but this app has no contact list, and the contract's
 * value is `shared_groups`. Naming it after something that does not exist would be a promise the
 * server cannot keep.
 *
 * "Who can message me" has THREE options and no "Nobody", which is not an omission. The contract's
 * `MessageAudience` has no `nobody` member because the setting gates conversation CREATION rather
 * than delivery -- someone already in a chat keeps being able to write whatever this says. So
 * `existing_chats` is the strictest reachable answer, and offering a fourth would be offering a
 * silence the server will not honour.
 *
 * Every change is written immediately and optimistically; [PrivacyViewModel] rolls the control
 * back if the write fails. It has to, because a privacy switch that stays where the user left it
 * while the server still holds the old value is not a cosmetic bug -- it is the screen telling
 * them they are hidden when they are not.
 */
@Composable
fun PrivacyScreen(
    settings: PrivacySettings?,
    onChange: (PrivacySettings) -> Unit,
    onBack: () -> Unit,
    onBlockedUsers: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * ALREADY-RENDERED WORDS, and the last parameter in this package that is.
     *
     * `PrivacyViewModel.error` is a `UiText`; the navigation host resolves it before it gets here.
     * That is one hop later than the rest of the package, which passes its state object through
     * whole -- but closing it means changing this signature, and the only call site is
     * `ui/Navigation.kt`, which this pass does not own. Left deliberately, recorded here rather
     * than as a TODO, because nothing about it is un-localised: the words on the other end of it
     * come from `profile_error_*` like every other failure on this surface.
     */
    error: String? = null,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** False until the first read settles, so an unloaded screen is not called unavailable. */
    loadedOnce: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors

    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(title = stringResource(Res.string.profile_privacy_title), onBack = onBack)

        // Above the controls, never instead of them: a failed save has to leave the settings
        // visible so the user can see what they were rolled back to.
        if (error != null) {
            SwetErrorBanner(message = error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            settings == null && !loadedOnce -> SwetLoading()

            settings == null -> SwetEmptyState(
                title = stringResource(Res.string.profile_privacy_unavailable_title),
                body = stringResource(Res.string.profile_privacy_unavailable_body),
                // An `if` rather than `onRetry?.let { stringResource(...) }`, as `ProfileScreen`
                // spells the same decision: the lookup is a composable call, and hiding one inside
                // a lambda that only sometimes runs is needless subtlety.
                actionText = if (onRetry != null) stringResource(Res.string.profile_try_again) else null,
                onAction = onRetry,
            )

            else -> Column(
                modifier = Modifier
                    .fillMaxSize()
                    // The gesture bar / three-button bar. This screen is a pushed DETAIL route,
                    // so the tab bar -- which is what applies this inset on a tab screen -- is not
                    // under it, and nothing else in the frame claims the bottom edge (see
                    // `ui/Navigation.kt`). Without it the last control sits under the system
                    // buttons and cannot be scrolled clear of them.
                    //
                    // BEFORE `verticalScroll`, so it insets the viewport rather than the content:
                    // the closing line then scrolls fully into view above the bar instead of
                    // arriving with the bar drawn over its descenders.
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin),
            ) {
                Spacer(Modifier.height(Space.one))

                ChoiceGroup(
                    title = stringResource(Res.string.profile_privacy_show_online),
                    options = PresenceAudienceOptions,
                    selected = settings.showOnline,
                    onSelect = { onChange(settings.copy(showOnline = it)) },
                )

                ChoiceGroup(
                    title = stringResource(Res.string.profile_privacy_show_last_seen),
                    options = PresenceAudienceOptions,
                    selected = settings.showLastSeen,
                    onSelect = { onChange(settings.copy(showLastSeen = it)) },
                )

                ChoiceGroup(
                    title = stringResource(Res.string.profile_privacy_show_date_of_birth),
                    options = PresenceAudienceOptions,
                    selected = settings.showDateOfBirth,
                    onSelect = { onChange(settings.copy(showDateOfBirth = it)) },
                )

                ChoiceGroup(
                    title = stringResource(Res.string.profile_privacy_show_location),
                    options = PresenceAudienceOptions,
                    selected = settings.showLocation,
                    onSelect = { onChange(settings.copy(showLocation = it)) },
                )

                ChoiceGroup(
                    title = stringResource(Res.string.profile_privacy_who_can_message),
                    options = MessageAudienceOptions,
                    selected = settings.allowMessagesFrom,
                    onSelect = { onChange(settings.copy(allowMessagesFrom = it)) },
                    // Said plainly, because the missing "Nobody" is the first thing a careful
                    // user will look for.
                    footnote = stringResource(Res.string.profile_privacy_message_footnote),
                )

                ProfileSectionHeader(stringResource(Res.string.profile_privacy_section_profile_link))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    ProfileControlRow(
                        title = stringResource(Res.string.profile_privacy_allow_profile_link),
                        // Says only what the switch does. It hides `profile_url` from other
                        // people's view of your profile; it does NOT stop your QR code working,
                        // which stays authenticated either way (DECISIONS §4) -- and the QR
                        // screen offering to unhide the link would break if it did.
                        supporting = stringResource(
                            Res.string.profile_privacy_allow_profile_link_supporting,
                        ),
                        // The whole row toggles, not just the 52dp switch: SDS §6.3 wants a 44dp
                        // target, and `Role.Switch` is what makes a screen reader announce it as
                        // one rather than as a button beside a picture.
                        modifier = Modifier.toggleable(
                            value = settings.allowProfileLink,
                            role = Role.Switch,
                            onValueChange = { onChange(settings.copy(allowProfileLink = it)) },
                        ),
                    ) {
                        // Inert: the row above owns the interaction, so the switch must not be a
                        // second focusable node saying the same thing.
                        Switch(checked = settings.allowProfileLink, onCheckedChange = null)
                    }
                }

                Spacer(Modifier.height(Space.two))

                ProfileSectionHeader(stringResource(Res.string.profile_privacy_section_safety))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    ProfileNavRow(
                        title = stringResource(Res.string.profile_blocked_users_title),
                        onClick = onBlockedUsers,
                    )
                }

                Spacer(Modifier.height(Space.three))

                // SPS §16.5. Stating the absence is allowed and honest; the disallowed thing is
                // claiming a secrecy the product does not have.
                Text(
                    text = stringResource(Res.string.profile_privacy_no_e2e),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textSecondary,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = Space.two),
                )

                Spacer(Modifier.height(Space.six))
            }
        }
    }
}

// ---------------------------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------------------------

/**
 * The option lists, declared once rather than inline, so the two presence controls cannot end up
 * offering different words for the same stored value.
 *
 * A `StringResource` RATHER THAN THE WORDS, which is what lets these stay top-level `val`s. A
 * resource id is an ordinary value in commonMain -- only the `stringResource()` lookup is
 * composable -- so the list of what is offered is still declared once, at file scope, and
 * [ChoiceGroup] resolves each label as it draws the row. Inlining the lookups into the two call
 * sites would have been the obvious way to make this compile, and it is exactly the drift this
 * shared list exists to prevent: `show_online` and `show_last_seen` are the same three scopes and
 * must read as the same three words.
 *
 * THE SCOPE WORDS ARE THE ONE PLACE ON THIS SCREEN WHERE A MISREADING COSTS SOMETHING. Each names a
 * SET OF PEOPLE, so `shared_groups` is "People in my groups" and never "Contacts" -- this product
 * has no contact list, and the softer word would name a set the server does not store. The Russian
 * file carries the same argument at the same block.
 */
private val PresenceAudienceOptions: List<Pair<PresenceAudience, StringResource>> = listOf(
    PresenceAudience.EVERYONE to Res.string.profile_privacy_audience_everyone,
    PresenceAudience.SHARED_GROUPS to Res.string.profile_privacy_audience_shared_groups,
    PresenceAudience.NOBODY to Res.string.profile_privacy_audience_nobody,
)

private val MessageAudienceOptions: List<Pair<MessageAudience, StringResource>> = listOf(
    MessageAudience.EVERYONE to Res.string.profile_privacy_audience_everyone,
    MessageAudience.SHARED_GROUPS to Res.string.profile_privacy_audience_shared_groups,
    MessageAudience.EXISTING_CHATS to Res.string.profile_privacy_audience_existing_chats,
)

/**
 * One titled group of mutually exclusive options.
 *
 * `selectableGroup` on the container is what makes assistive technology announce "2 of 3" instead
 * of reading three unrelated radio buttons; the row carries the click and the role, and the
 * button itself stays inert (§22).
 */
@Composable
private fun <T> ChoiceGroup(
    title: String,
    options: List<Pair<T, StringResource>>,
    selected: T,
    onSelect: (T) -> Unit,
    modifier: Modifier = Modifier,
    footnote: String? = null,
) {
    Column(modifier) {
        ProfileSectionHeader(title)

        SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
            Column(Modifier.selectableGroup()) {
                options.forEach { (value, label) ->
                    val isSelected = value == selected
                    ProfileControlRow(
                        // Resolved per row. `forEach` over a `List` is an inline call, so this is
                        // an ordinary composable call site and not a lambda Compose has to reason
                        // about -- and the list is a fixed three or four entries, in a fixed order,
                        // so the composition sees the same calls in the same order every time.
                        title = stringResource(label),
                        modifier = Modifier.selectable(
                            selected = isSelected,
                            role = Role.RadioButton,
                            // Re-selecting the current option would send a write that changes
                            // nothing -- and, because every write is a full replacement, would
                            // still be a full round trip.
                            onClick = { if (!isSelected) onSelect(value) },
                        ),
                    ) {
                        RadioButton(selected = isSelected, onClick = null)
                    }
                }
            }
        }

        if (footnote != null) {
            Text(
                text = footnote,
                style = MaterialTheme.typography.labelMedium,
                color = SwetgramThemeTokens.colors.textSecondary,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = Space.two, vertical = Space.one),
            )
        }

        Spacer(Modifier.height(Space.two))
    }
}
